import { registerPageInit } from '../barba/page-init-registry';
import '../../css/_root.scss';
import '../../css/pages/education/index.scss';

import { FormWidget } from '../widgets/form-widget';
import { initPermissionRequest } from '../widgets/permission-request';
import { beforeSubmit } from '@/js/utils/beforeSubmit';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { initializePhoneMask } from '@/js/utils/initializePhoneMask';
import { trackYaGoal } from '@/js/utils/yandex-metrika';
import notificationsWordings from '@/wordings/notificationWordings.json';

class EducationPermission {
  private educationPermissionFormWidget: FormWidget;
  private multiselectData: Map<string, { values: string[]; labels: string[] }> = new Map();

  constructor() {
    const { educationPermissionForm } = notificationsWordings;
    const successMessageEl = document.createElement('div');
    const errorMessageEl = document.createElement('div');

    successMessageEl.innerHTML = `
      <p class='toast-template__title'>${educationPermissionForm.title}</p>
      <p class='toast-template__description'>
      ${educationPermissionForm.description}
      </p>
    `;

    errorMessageEl.innerHTML = `
      <p class='toast-template__title'>${educationPermissionForm.errorTitle}</p>
      <p class='toast-template__description'>
         ${educationPermissionForm.errorDescription}
      </p>
    `;

    this.educationPermissionFormWidget = new FormWidget({
      formSelector: '[id^="educationPermissionForm"]',
      submitUrl: getApiUrl('/api/educationPermissionForm.php'),
      successMessage: successMessageEl,
      errorMessage: errorMessageEl,
      beforeSubmit: async (form: HTMLFormElement) => {
        const isValid = this.validateAllFields(form);

        if (!isValid) return false;

        return beforeSubmit(form);
      },

      onSubmitting: (isSubmitting: boolean) => {
        if (!isSubmitting) {
          // Сбрасываем данные мультиселекта после успешной отправки
          this.resetMultiselectData();
        }
      },
      onSuccess: () => {
        trackYaGoal('submitApplication');
      },
    });
  }

  public init(root?: ParentNode): void {
    initPermissionRequest(root);
    this.educationPermissionFormWidget.init();
    this.initCustomMultiselect();
    initializePhoneMask('contact_phone');
  }

  // Новый метод для сброса данных мультиселекта
  private resetMultiselectData(): void {
    // Сбрасываем данные в Map
    this.multiselectData.forEach((data) => {
      data.values = [];
      data.labels = [];
    });

    // Сбрасываем визуальное состояние всех мультиселектов
    const multiselects = document.querySelectorAll<HTMLElement>('.custom-multiselect');

    multiselects.forEach((multiselect: HTMLElement) => {
      const multiselectId = multiselect.getAttribute('data-multiselect-id');
      if (!multiselectId) return;

      const toggle = multiselect.querySelector<HTMLButtonElement>('.custom-multiselect-toggle');
      const menu = multiselect.querySelector<HTMLElement>('.custom-multiselect-menu');
      const options = multiselect.querySelectorAll<HTMLElement>('.custom-multiselect-option');
      const selectedText = multiselect.querySelector<HTMLElement>('.selected-text');
      const hiddenInput = multiselect.querySelector<HTMLInputElement>('input[type="hidden"]');

      if (!toggle || !menu || !selectedText || !hiddenInput) return;

      // Сбрасываем визуальное состояние
      selectedText.textContent = 'Выберите варианты...';
      selectedText.title = '';

      // Сбрасываем выделение опций
      options.forEach((option: HTMLElement) => {
        option.classList.remove('selected');
      });

      // Сбрасываем hidden input
      hiddenInput.value = '';

      // Удаляем дополнительные hidden inputs
      const existingHiddenInputs = multiselect.querySelectorAll<HTMLInputElement>(
        'input[name*="education_types"]',
      );
      existingHiddenInputs.forEach((input: HTMLInputElement) => {
        if (input !== hiddenInput) {
          input.remove();
        }
      });

      // Сбрасываем валидацию
      toggle.classList.remove('is-invalid');
    });

    // Сбрасываем чекбоксы "Формы обучения"
    const educationFormsCheckboxes = document.querySelectorAll<HTMLInputElement>(
      'input[name="education_forms"]',
    );
    educationFormsCheckboxes.forEach((checkbox: HTMLInputElement) => {
      checkbox.checked = false;
      checkbox.classList.remove('is-invalid');
    });

    // Скрываем сообщение об ошибке для чекбоксов форм обучения
    const educationFormsContainer = document
      .querySelector('.education-forms-checkboxes')
      ?.closest('.row');
    if (educationFormsContainer) {
      const feedback = educationFormsContainer.querySelector<HTMLElement>('.invalid-feedback');
      if (feedback) {
        feedback.style.display = 'none';
      }
    }

    // Сбрасываем чекбокс согласия с политикой
    const privacyPolicyCheckbox = document.querySelector<HTMLInputElement>(
      'input[name="privacy_policy"]',
    );
    if (privacyPolicyCheckbox) {
      privacyPolicyCheckbox.checked = false;
      privacyPolicyCheckbox.classList.remove('is-invalid');
    }
  }

  private validateAllFields(form: HTMLFormElement): boolean {
    let isValid = true;

    // Сбрасываем все ошибки
    this.resetValidationErrors();

    // 1. Проверяем обычные поля ввода
    const requiredInputs = form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      'input[required], textarea[required]',
    );

    requiredInputs.forEach((input: HTMLInputElement | HTMLTextAreaElement) => {
      // Пропускаем hidden inputs и чекбоксы с privacy_policy
      if (input.type === 'hidden' || input.name === 'privacy_policy') {
        return;
      }

      if (!input.value.trim()) {
        this.markFieldAsInvalid(input);
        isValid = false;
      }
    });

    // 2. Проверяем чекбокс privacy_policy
    const privacyPolicyCheckbox = form.querySelector<HTMLInputElement>(
      'input[name="privacy_policy"]',
    );
    if (privacyPolicyCheckbox && !privacyPolicyCheckbox.checked) {
      this.markFieldAsInvalid(privacyPolicyCheckbox);
      isValid = false;
    }

    // 3. Проверяем чекбоксы "Формы обучения"
    const educationFormsValid = this.validateEducationForms(form);
    if (!educationFormsValid) {
      isValid = false;
    }

    // 4. Проверяем мультиселект
    const multiselectValid = this.validateAllMultiselects();
    if (!multiselectValid) {
      isValid = false;
    }

    // 5. Проверяем email на валидность
    const emailInput = form.querySelector<HTMLInputElement>('input[type="email"][required]');
    if (emailInput && emailInput.value.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailInput.value.trim())) {
        this.markFieldAsInvalid(emailInput, 'Пожалуйста, введите корректный email адрес');
        isValid = false;
      }
    }

    // 6. Проверяем телефон на валидность
    const phoneInput = form.querySelector<HTMLInputElement>('input[type="tel"][required]');
    if (phoneInput && phoneInput.value.trim()) {
      // Простая проверка телефона (минимум 10 цифр)
      const phoneDigits = phoneInput.value.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        this.markFieldAsInvalid(phoneInput, 'Пожалуйста, введите корректный номер телефона');
        isValid = false;
      }
    }

    // 7. Проверяем дату регистрации
    const dateInput = form.querySelector<HTMLInputElement>('input[type="date"][required]');
    if (dateInput && !dateInput.value) {
      this.markFieldAsInvalid(dateInput);
      isValid = false;
    }

    // Прокручиваем к первой ошибке
    if (!isValid) {
      const firstError = form.querySelector('.is-invalid');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    return isValid;
  }

  private resetValidationErrors(): void {
    const form = document.getElementById('educationPermissionForm');
    if (!form) return;

    // Сбрасываем ошибки для обычных полей
    const allFields = form.querySelectorAll<HTMLElement>('input, textarea, select');

    allFields.forEach((field: HTMLElement) => {
      field.classList.remove('is-invalid');
    });

    // Сбрасываем ошибки для кастомного мультиселекта
    const multiselectToggles = form.querySelectorAll<HTMLElement>('.custom-multiselect-toggle');
    multiselectToggles.forEach((toggle: HTMLElement) => {
      toggle.classList.remove('is-invalid');
    });

    // Сбрасываем ошибки для чекбоксов форм обучения
    const educationFormsContainer = form
      .querySelector('.education-forms-checkboxes')
      ?.closest('.row');
    if (educationFormsContainer) {
      const feedback = educationFormsContainer.querySelector<HTMLElement>('.invalid-feedback');
      if (feedback) {
        feedback.style.display = 'none';
      }
    }
  }

  private markFieldAsInvalid(field: HTMLElement, customMessage?: string): void {
    field.classList.add('is-invalid');

    // Показываем сообщение об ошибке, если есть
    if (customMessage) {
      const feedback = field.nextElementSibling;
      if (feedback && feedback.classList.contains('invalid-feedback')) {
        (feedback as HTMLElement).textContent = customMessage;
      }
    }
  }

  private validateEducationForms(form: HTMLFormElement): boolean {
    const checkboxes = form.querySelectorAll<HTMLInputElement>('input[name="education_forms"]');
    const checked = Array.from(checkboxes).some((cb) => cb.checked);
    const container = checkboxes[0]?.closest('.row');

    if (!checked && container) {
      const feedback = container.querySelector<HTMLElement>('.invalid-feedback');
      if (feedback) {
        feedback.style.display = 'block';
      }

      // Помечаем все чекбоксы как невалидные
      checkboxes.forEach((checkbox) => {
        checkbox.classList.add('is-invalid');
      });

      return false;
    }

    return true;
  }

  private initCustomMultiselect(): void {
    const multiselects = document.querySelectorAll<HTMLElement>('.custom-multiselect');

    multiselects.forEach((multiselect: HTMLElement) => {
      const multiselectId = multiselect.getAttribute('data-multiselect-id');
      if (!multiselectId) return;

      const toggle = multiselect.querySelector<HTMLButtonElement>('.custom-multiselect-toggle');
      const menu = multiselect.querySelector<HTMLElement>('.custom-multiselect-menu');
      const options = multiselect.querySelectorAll<HTMLElement>('.custom-multiselect-option');
      const selectedText = multiselect.querySelector<HTMLElement>('.selected-text');
      const hiddenInput = multiselect.querySelector<HTMLInputElement>('input[type="hidden"]');

      if (!toggle || !menu || !selectedText || !hiddenInput) return;

      // Инициализируем данные для этого мультиселекта
      this.multiselectData.set(multiselectId, { values: [], labels: [] });

      const updateSelectedText = (): void => {
        const data = this.multiselectData.get(multiselectId);
        if (!data) return;

        if (data.labels.length > 0) {
          const displayText = data.labels.join(', ');
          selectedText.textContent =
            displayText.length > 50 ? displayText.substring(0, 47) + '...' : displayText;
          selectedText.title = data.labels.join(', ');
        } else {
          selectedText.textContent = 'Выберите варианты...';
          selectedText.title = '';
        }
      };

      const updateHiddenInput = (): void => {
        const data = this.multiselectData.get(multiselectId);
        if (!data) return;

        // Удаляем все старые hidden inputs
        const existingHiddenInputs = multiselect.querySelectorAll<HTMLInputElement>(
          'input[name*="education_types"]',
        );
        existingHiddenInputs.forEach((input: HTMLInputElement) => {
          if (input !== hiddenInput) {
            input.remove();
          }
        });

        // Добавляем новый hidden input для каждого значения
        data.values.forEach((value, index) => {
          if (index === 0) {
            hiddenInput.value = value;
          } else {
            const newInput = document.createElement('input');
            newInput.type = 'hidden';
            newInput.name = hiddenInput.name;
            newInput.value = value;
            hiddenInput.parentNode?.insertBefore(newInput, hiddenInput.nextSibling);
          }
        });

        // Если ничего не выбрано, очищаем hidden input
        if (data.values.length === 0) {
          hiddenInput.value = '';
        }
      };

      const updateOptionSelection = (): void => {
        const data = this.multiselectData.get(multiselectId);
        if (!data) return;

        options.forEach((option: HTMLElement) => {
          const value = option.getAttribute('data-value');
          if (value && data.values.includes(value)) {
            option.classList.add('selected');
          } else {
            option.classList.remove('selected');
          }
        });
      };

      const handleOptionClick = (option: HTMLElement): void => {
        const data = this.multiselectData.get(multiselectId);
        if (!data) return;

        const value = option.getAttribute('data-value');
        const label = option.getAttribute('data-label');

        if (!value || !label) return;

        const index = data.values.indexOf(value);

        if (index === -1) {
          // Добавляем значение
          data.values.push(value);
          data.labels.push(label);
        } else {
          // Удаляем значение
          data.values.splice(index, 1);
          data.labels.splice(index, 1);
        }

        updateOptionSelection();
        updateSelectedText();
        updateHiddenInput();

        // Убираем инвалидное состояние если что-то выбрано
        if (data.values.length > 0) {
          toggle.classList.remove('is-invalid');
        }
      };

      // Добавляем обработчики клика на опции
      options.forEach((option: HTMLElement) => {
        option.addEventListener('click', () => {
          handleOptionClick(option);
        });
      });

      // Обработчик открытия/закрытия меню
      toggle.addEventListener('click', (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Переключаем видимость меню
        if (menu.classList.contains('show')) {
          menu.classList.remove('show');
          toggle.setAttribute('aria-expanded', 'false');
        } else {
          // Закрываем все другие открытые меню
          document
            .querySelectorAll('.custom-multiselect-menu.show')
            .forEach((openMenu: Element) => {
              if (openMenu !== menu) {
                openMenu.classList.remove('show');
                const openToggle = document.querySelector<HTMLElement>(
                  `[aria-labelledby="${openMenu.getAttribute('aria-labelledby')}"]`,
                );
                if (openToggle) {
                  openToggle.setAttribute('aria-expanded', 'false');
                }
              }
            });

          menu.classList.add('show');
          toggle.setAttribute('aria-expanded', 'true');
        }
      });

      // Закрытие меню при клике вне его
      document.addEventListener('click', (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!multiselect.contains(target)) {
          menu.classList.remove('show');
          toggle.setAttribute('aria-expanded', 'false');
        }
      });

      // Инициализируем начальное состояние
      updateSelectedText();
      updateOptionSelection();
      updateHiddenInput();
    });
  }

  private validateAllMultiselects(): boolean {
    let isValid = true;
    const multiselects = document.querySelectorAll<HTMLElement>('.custom-multiselect');

    multiselects.forEach((multiselect: HTMLElement) => {
      const multiselectId = multiselect.getAttribute('data-multiselect-id');
      if (!multiselectId) return;

      const toggle = multiselect.querySelector<HTMLButtonElement>('.custom-multiselect-toggle');
      if (!toggle) return;

      const data = this.multiselectData.get(multiselectId);
      if (!data) return;

      const isRequired = multiselect.querySelector('.form-label.required') !== null;

      if (isRequired && data.values.length === 0) {
        toggle.classList.add('is-invalid');
        isValid = false;
      } else {
        toggle.classList.remove('is-invalid');
      }
    });

    return isValid;
  }
}

registerPageInit('education-permission', (root?: ParentNode) => {
  const page = new EducationPermission();
  page.init(root);
});
